import { apiRequest } from '@/shared/api/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type { CrmActivity, CrmActivityTipo, CrmEntry, CrmStage } from '../model/types';

type ApiCrmEntryDto = {
  id: string;
  ownerId: string;
  contactId: string | null;
  matchId: string | null;
  nomeContato: string;
  empresaContato: string | null;
  whatsappContato: string | null;
  stage: CrmStage;
  dealValue: number;
  notas: string | null;
  agingDays: number;
  isCold: boolean;
  createdAt: string;
  updatedAt: string;
};

type ApiKanbanDto = {
  columns: Record<CrmStage, ApiCrmEntryDto[]>;
  totals: Record<CrmStage, number>;
};

type ApiActivityDto = {
  id: string;
  crmEntryId: string;
  userId: string;
  tipo: string;
  descricao: string;
  metadata: Record<string, unknown>;
  createdAt: string;
};

const KNOWN_ACTIVITY_TIPOS: CrmActivityTipo[] = [
  'NOTA',
  'REUNIAO',
  'VALOR_INFORMADO',
  'PROXIMO_PASSO',
  'VOICE_TASK',
  'MATCH_ACEITO',
  'STAGE_CHANGE',
  'LIGACAO',
  'EMAIL',
  'OUTRO',
];

const normalizeActivityTipo = (tipo: string): CrmActivityTipo =>
  KNOWN_ACTIVITY_TIPOS.includes(tipo as CrmActivityTipo) ? (tipo as CrmActivityTipo) : 'OUTRO';

const fromCrmDto = (dto: ApiCrmEntryDto): CrmEntry => ({
  id: dto.id,
  matchId: dto.matchId ?? '',
  participanteId: dto.contactId ?? '',
  participanteNome: dto.nomeContato,
  participanteFotoUrl: null,
  stage: dto.stage,
  valorPotencial: dto.dealValue,
  atividades: [],
  createdAt: dto.createdAt,
  updatedAt: dto.updatedAt,
});

const fromActivityDto = (dto: ApiActivityDto): CrmActivity => ({
  id: dto.id,
  tipo: normalizeActivityTipo(dto.tipo),
  descricao: dto.descricao,
  createdAt: dto.createdAt,
});

export const crmApi = {
  list: async (): Promise<{ entries: CrmEntry[] }> => {
    const out = await apiRequest<ApiKanbanDto>({
      method: 'GET',
      url: ENDPOINTS.CRM.LIST_ME,
    });
    const entries: CrmEntry[] = [];
    for (const stage of Object.keys(out.columns) as CrmStage[]) {
      for (const e of out.columns[stage] ?? []) {
        entries.push(fromCrmDto(e));
      }
    }
    return { entries };
  },
  getById: async (id: string): Promise<{ entry: CrmEntry }> => {
    const out = await apiRequest<{ entry: ApiCrmEntryDto; activities: ApiActivityDto[] }>({
      method: 'GET',
      url: ENDPOINTS.CRM.BY_ID(id),
    });
    return {
      entry: {
        ...fromCrmDto(out.entry),
        atividades: out.activities.map(fromActivityDto),
      },
    };
  },
  updateStage: async (id: string, stage: CrmStage): Promise<{ id: string; stage: CrmStage }> => {
    const out = await apiRequest<{ id: string; stage: CrmStage; previousStage: CrmStage }>({
      method: 'PATCH',
      url: ENDPOINTS.CRM.STAGE(id),
      data: { stage },
    });
    return { id: out.id, stage: out.stage };
  },
  addActivity: async (
    id: string,
    input: { tipo: 'NOTA' | 'REUNIAO' | 'VALOR_INFORMADO' | 'PROXIMO_PASSO'; descricao: string },
  ): Promise<{ activity: CrmActivity }> => {
    const out = await apiRequest<ApiActivityDto>({
      method: 'POST',
      url: ENDPOINTS.CRM.ACTIVITIES(id),
      data: input,
    });
    return { activity: fromActivityDto(out) };
  },
};
